'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { useReviewNotifications } from '@/hooks/useReview'
import { reviewApi } from '@/lib/ap/review'
import type { ReviewNotification } from '@/lib/ap/review'
import { badgeText, relativeAge } from '@/lib/review-view'
import { cn } from '@/lib/utils'

/**
 * The buyer's in-app notification bell (A4), in the reviewer shell's actions
 * slot: one entry per request assigned to the signed-in reviewer, unread until
 * they open it. The list loads when the bell opens; the count comes from the
 * shell (`/api/review/notifications/unread-count`), so the bell and the
 * "My approvals" badge never disagree.
 */
export function ReviewNotificationBell({ unread, onChanged }: { unread: number; onChanged: () => void }) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const list = useReviewNotifications(open)
  const items = list.data?.items ?? []
  const badge = badgeText(unread)

  const markSeen = async (ids?: number[]) => {
    try {
      await reviewApi.markSeen(ids)
    } catch {
      // Best effort: the badge simply stays until the next refresh.
    }
    onChanged()
    void list.refresh()
  }

  const openItem = (item: ReviewNotification) => {
    setOpen(false)
    if (item.assignment_id != null) {
      void markSeen([item.assignment_id])
      router.push(`/review/${item.assignment_id}`)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={badge ? `Notifications, ${badge} unread` : 'Notifications'}
          className='relative inline-flex h-11 w-11 items-center justify-center rounded-lg text-white/85 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower'
        >
          <Icons.bell className='h-5 w-5' aria-hidden='true' />
          {badge && (
            <span
              aria-hidden='true'
              className='absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-cornflower px-1 text-micro tracking-normal text-brand-navy'
            >
              {badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* Count changes are announced once, here, instead of re-reading the whole list. */}
      <span className='sr-only' role='status' aria-live='polite'>
        {badge ? `${badge} unread notifications` : ''}
      </span>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b border-hairline px-4 py-3'>
          <h2 className='font-display text-sm font-semibold text-foreground'>Notifications</h2>
          {unread > 0 && (
            <button
              type='button'
              onClick={() => void markSeen()}
              className='rounded text-xs font-medium text-brand-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            >
              Mark all as seen
            </button>
          )}
        </div>
        <div className='max-h-80 overflow-y-auto' aria-busy={list.loading}>
          {!list.data && !list.error ? (
            <ul className='divide-y divide-hairline' aria-label='Loading notifications'>
              {[0, 1, 2].map((i) => (
                <li key={i} className='flex gap-3 px-4 py-3'>
                  <Skeleton variant='circular' className='mt-1.5 h-2 w-2' />
                  <div className='flex-1 space-y-1.5'>
                    <Skeleton variant='text' className='h-3.5 w-3/4' />
                    <Skeleton variant='text' className='h-3 w-1/2' />
                  </div>
                </li>
              ))}
            </ul>
          ) : list.error && !list.data ? (
            <div className='space-y-2 px-4 py-6 text-center text-sm'>
              <p className='text-danger-strong'>Could not load notifications.</p>
              <button
                type='button'
                onClick={() => void list.refresh()}
                className='rounded text-xs font-medium text-brand-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              >
                Retry
              </button>
            </div>
          ) : items.length === 0 ? (
            <p className='px-4 py-6 text-center text-sm text-muted-foreground'>No notifications yet.</p>
          ) : (
            <ul className='divide-y divide-hairline'>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type='button'
                    onClick={() => openItem(item)}
                    className={cn(
                      'flex w-full gap-3 px-4 py-3 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      item.unread && 'bg-brand-cornflower/10'
                    )}
                  >
                    <span
                      aria-hidden='true'
                      className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', item.unread ? 'bg-brand-purple' : 'bg-transparent')}
                    />
                    <span className='min-w-0 flex-1'>
                      <span className='block text-sm font-medium text-foreground'>
                        {item.title}
                        {item.unread && <span className='sr-only'> (unread)</span>}
                      </span>
                      {(item.vendor_name || item.message) && (
                        <span className='mt-0.5 block truncate text-xs text-muted-foreground'>
                          {[item.vendor_name, item.message].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {item.created_at && (
                        <span className='mt-0.5 block text-xs text-muted-foreground'>{relativeAge(item.created_at)}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
